import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select"; // ✅ react-select
import { useTheme } from "./ThemeContext";
import { apiClient } from "../utils/const";
import DeletePayment from "./DeletePayment";
import DeleteWithdrawal from "./DeleteWithdrawal";
import EditPayment from "./EditPayment";
import EditWithdrawal from "./EditWithdrawal";

const formatInr = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN")}`;

const printInHiddenFrame = (html) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.visibility = "hidden";
  document.body.appendChild(iframe);

  const frameDoc = iframe.contentWindow?.document;
  if (!frameDoc) {
    document.body.removeChild(iframe);
    return false;
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    }, 1000);
  }, 250);

  return true;
};

function Report() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // stores full option {value,label}
  const [allTransactions, setAllTransactions] = useState([]); // All transactions
  const [filteredTransactions, setFilteredTransactions] = useState([]); // Filtered transactions
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editingWithdrawalId, setEditingWithdrawalId] = useState(null);

  const [schemeType, setSchemeType] = useState(
    () => localStorage.getItem("schemeType") || "mandal"
  );
  const { dark } = useTheme();

  const normalizeMethod = (value) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    if (v === "cash") return "Cash";
    if (v === "upi") return "UPI";
    if (v === "cheque" || v === "check" || v === "bank transfer") return "Bank Transfer";
    if (v === "banktransfer" || v === "bank_transfer") return "Bank Transfer";
    if (v === "net banking" || v === "netbanking") return "Net Banking";
    if (v === "metal") return "Metal";
    return value;
  };

  // ---------- utils ----------
  const getFullName = (user) =>
    [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");
  const resolveId = (user) => user?.id ?? user?.user_id ?? user?.uid ?? user?.mid;

  // ---------- data: combined fetch ----------
  const fetchReportData = async () => {
    setLoadingUsers(true);
    setLoadingTransactions(true);
    try {
      const apiUrl =
        schemeType === "kanchan"
          ? "/kanchan/kanchan_report.php"
          : "/mandal/mandal_report.php";
      const { data } = await apiClient.post(apiUrl);
      // console.log(data.data);

      // Check if response message is 'radha'
      if (data?.message === 'radha') {
        // Extract users data
        const usersArr = Array.isArray(data?.data?.users)
          ? data.data.users
          : Array.isArray(data?.users)
          ? data.users
          : [];

        // Extract transactions data
        const transactions = Array.isArray(data?.data?.transactions)
          ? data.data.transactions
          : Array.isArray(data?.transactions)
          ? data.transactions
          : [];

        setUsers(usersArr);
        setAllTransactions(transactions);
        if (selectedUser) {
          const uIdStr = String(selectedUser.value);
          setFilteredTransactions(
            transactions.filter((tx) => String(tx.user_id) === uIdStr)
          );
        } else {
          setFilteredTransactions(transactions);
        }
      } else {
        console.error("Invalid response message:", data?.message);
        setUsers([]);
        setAllTransactions([]);
        setFilteredTransactions([]);
      }
    } catch (error) {
      console.error("Failed to fetch report data:", error);
      setUsers([]);
      setAllTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoadingUsers(false);
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeType]);

  useEffect(() => {
    const handleSchemeChange = () => {
      setSchemeType(localStorage.getItem("schemeType") || "mandal");
    };
    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  // ---------- handlers ----------
  const handleUserChange = (option) => {
    setSelectedUser(option);
    setCurrentPage(1);
    if (option) {
      const uIdStr = String(option.value);
      const filtered = allTransactions.filter((tx) => String(tx.user_id) === uIdStr);
      setFilteredTransactions(filtered);
    } else {
      setFilteredTransactions(allTransactions); // Show all transactions when no user selected
    }
  };

  const handleDeleteSuccess = (deletedId) => {
    setAllTransactions((prev) => {
      const next = prev.filter((tx) => String(tx.id) !== String(deletedId));
      if (selectedUser) {
        const uIdStr = String(selectedUser.value);
        setFilteredTransactions(
          next.filter((tx) => String(tx.user_id) === uIdStr)
        );
      } else {
        setFilteredTransactions(next);
      }
      return next;
    });
  };

  // ---------- select options ----------
  const cardNumberByUserId = useMemo(() => {
    const map = {};
    users.forEach((user) => {
      const id = resolveId(user);
      if (id !== undefined && id !== null) {
        map[String(id)] = user.card_no || "";
      }
    });
    return map;
  }, [users]);

  const selectStyles = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        minHeight: 44,
        backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#fff",
        borderColor: state.isFocused ? "var(--bs-primary)" : "var(--bs-border-color)",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(102, 126, 234, 0.15)" : "none",
        color: dark ? "#f1f1f1" : "#212529",
        borderRadius: 12,
        transition: "all 0.2s ease",
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "6px 12px",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: dark ? "#f1f1f1" : "#212529",
        fontWeight: 500,
      }),
      input: (provided) => ({
        ...provided,
        color: dark ? "#f1f1f1" : "#212529",
      }),
      placeholder: (provided) => ({
        ...provided,
        color: dark ? "rgba(255,255,255,0.5)" : "#6c757d",
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        color: state.isFocused ? "var(--bs-primary)" : dark ? "rgba(255,255,255,0.6)" : "#6c757d",
        paddingRight: 12,
      }),
      clearIndicator: (provided) => ({
        ...provided,
        paddingRight: 12,
        color: dark ? "rgba(255,255,255,0.6)" : "#6c757d",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: dark ? "#1f1f1f" : "#fff",
        color: dark ? "#f1f1f1" : "#212529",
        border: "1px solid var(--bs-border-color)",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
        marginTop: 8,
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "var(--bs-primary)"
          : state.isFocused
          ? "var(--bs-primary-bg-subtle)"
          : "transparent",
        color: state.isSelected ? "#fff" : dark ? "#f1f1f1" : "#212529",
        fontWeight: state.isSelected ? 600 : 500,
        padding: "10px 14px",
        transition: "background 0.15s ease",
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
    }),
    []
  );

  const menuPortalTarget =
    typeof window !== "undefined" ? document.body : undefined;

  const userOptions = useMemo(() => {
    return users.map((user) => {
      const id = resolveId(user);
      const cardNo = user.card_no || id;
      const name = getFullName(user);
      const phonePart = user.phone ? ` ${user.phone}` : "";
      return {
        value: String(id), // normalize to string
        label: `${cardNo}) ${name}${phonePart ? ` -${phonePart}` : ""}`,
        _raw: user,
      };
    });
  }, [users]);

  // ---------- pagination ----------
  const totalPages = Math.ceil(filteredTransactions.length / entriesPerPage) || 1;
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return filteredTransactions.slice(start, start + entriesPerPage);
  }, [filteredTransactions, currentPage, entriesPerPage]);

  // ---------- overall summary calculations (when no user selected) ----------
  const overallSummary = useMemo(() => {
    if (selectedUser || allTransactions.length === 0) return null;
    
    const totalPayments = allTransactions
      .filter(tx => tx.transaction_type === 'payment')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    const totalWithdrawals = allTransactions
      .filter(tx => tx.transaction_type === 'withdrawal')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    const totalBalance = totalPayments - totalWithdrawals;
    
    // Additional calculations for overall summary
    const regularTx = allTransactions.filter(
      tx => tx.transaction_type === 'payment' && tx.transaction_method !== 'Waiting' && tx.transaction_method !== 'Skipped'
    );
    const regularPayments = regularTx.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    const regularCount = regularTx.length;
    
    const waitingTx = allTransactions.filter(
      tx => tx.transaction_type === 'payment' && tx.transaction_method === 'Waiting'
    );
    const waitingPayments = waitingTx.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    const waitingCount = waitingTx.length;
    
    const skippedTx = allTransactions.filter(
      tx => tx.transaction_type === 'payment' && tx.transaction_method === 'Skipped'
    );
    const skippedPayments = skippedTx.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    const skippedCount = skippedTx.length;
    
    const totalTransactions = allTransactions.length;
    const paymentCount = allTransactions.filter(tx => tx.transaction_type === 'payment').length;
    const withdrawalCount = allTransactions.filter(tx => tx.transaction_type === 'withdrawal').length;
    
    return {
      totalPayments,
      totalWithdrawals,
      totalBalance,
      regularPayments,
      waitingPayments,
      skippedPayments,
      totalTransactions,
      paymentCount,
      withdrawalCount,
      regularCount,
      waitingCount,
      skippedCount
    };
  }, [selectedUser, allTransactions]);

  // ---------- user summary calculations ----------
  const userSummary = useMemo(() => {
    if (!selectedUser) return null;
    
    const userTransactions = filteredTransactions.filter(tx => 
      String(tx.user_id) === String(selectedUser.value)
    );
    
    const totalPayments = userTransactions
      .filter(tx => tx.transaction_type === 'payment')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    const totalWithdrawals = userTransactions
      .filter(tx => tx.transaction_type === 'withdrawal')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    const totalBalance = totalPayments - totalWithdrawals;
    
    // Payment breakdown by type
    const regularPayments = userTransactions
      .filter(tx => tx.transaction_type === 'payment' && tx.transaction_method !== 'Waiting' && tx.transaction_method !== 'Skipped')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    const waitingPayments = userTransactions
      .filter(tx => tx.transaction_type === 'payment' && tx.transaction_method === 'Waiting')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    const skippedPayments = userTransactions
      .filter(tx => tx.transaction_type === 'payment' && tx.transaction_method === 'Skipped')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
    
    // Month counts
    const monthsPaid = userTransactions
      .filter(tx => tx.transaction_type === 'payment' && tx.transaction_method !== 'Waiting' && tx.transaction_method !== 'Skipped')
      .length;
    
    const waitingMonths = userTransactions
      .filter(tx => tx.transaction_type === 'payment' && tx.transaction_method === 'Waiting')
      .length;
    
    const skippedMonths = userTransactions
      .filter(tx => tx.transaction_type === 'payment' && tx.transaction_method === 'Skipped')
      .length;
    
    const totalTransactions = userTransactions.length;
    const paymentCount = userTransactions.filter(tx => tx.transaction_type === 'payment').length;
    const withdrawalCount = userTransactions.filter(tx => tx.transaction_type === 'withdrawal').length;
    
    return {
      totalPayments,
      totalWithdrawals,
      totalBalance,
      regularPayments,
      waitingPayments,
      skippedPayments,
      monthsPaid,
      waitingMonths,
      skippedMonths,
      totalTransactions,
      paymentCount,
      withdrawalCount
    };
  }, [selectedUser, filteredTransactions]);

  // ---------- badge helpers (same logic we used before) ----------
  const deriveColor = (tType, tMethod) => {
    if (tType === "withdrawal") return "#FF6C6E"; // red
    if (tType === "payment") {
      if (tMethod === "Waiting") return "#EEC0FF"; // purple
      if (tMethod === "Skipped") return "#FFDCAC"; // orange
      return "#5FD965"; // regular (green)
    }
    return "#6c757d"; // neutral grey fallback
  };

  const getTxnLabel = (tType, tMethod) =>
    tType === "payment"
      ? tMethod === "Waiting"
        ? "Waiting"
        : tMethod === "Skipped"
        ? "Skipped"
        : "Regular"
      : "Withdrawal";

  const handleGeneratePdf = () => {
    if (!selectedUser || !userSummary) return;

    const user = selectedUser._raw || {};
    const memberName = getFullName(user) || selectedUser.label || "-";
    const cardNo = user.card_no || cardNumberByUserId[String(selectedUser.value)] || "-";
    const phone = user.phone || "-";
    const schemeName = schemeType === "kanchan" ? "Kanchanvarsha" : "Mandal";
    const generatedOn = new Date().toLocaleString("en-IN");

    const rowsHtml =
      filteredTransactions.length > 0
        ? filteredTransactions
            .map((tx) => {
              const label = getTxnLabel(tx.transaction_type, tx.transaction_method);
              const method = normalizeMethod(tx.transaction_method);
              const methodLabel = ["Cash", "UPI", "Bank Transfer", "Net Banking", "Metal"].includes(
                method
              )
                ? method
                : "-";
              return `
                <tr>
                  <td>${tx.transaction_date || tx.payment_date || "-"}</td>
                  <td>${label}</td>
                  <td style="text-align:right;">${formatInr(tx.amount)}</td>
                  <td>${methodLabel}</td>
                </tr>`;
            })
            .join("")
        : `<tr><td colspan="4" style="text-align:center;">No transactions found</td></tr>`;

    const html = `<!doctype html>
      <html>
        <head>
          <title>Member Report - ${memberName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; margin: 0; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
            h2 { margin: 0 0 4px; }
            .meta { font-size: 13px; color: #444; line-height: 1.5; }
            .summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 16px 0 20px; }
            .tile { border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
            .tile .label { font-size: 12px; color: #666; margin-bottom: 6px; }
            .tile .value { font-size: 18px; font-weight: 700; }
            .tile .sub { font-size: 12px; color: #777; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
            h3 { margin: 0 0 8px; font-size: 15px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h2>${schemeName} - Member Report</h2>
              <div class="meta">
                <div><strong>Card No:</strong> ${cardNo}</div>
                <div><strong>Member:</strong> ${memberName}${phone && phone !== "-" ? ` (${phone})` : ""}</div>
              </div>
            </div>
          </div>

          <div class="summary">
            <div class="tile">
              <div class="label">Total Balance</div>
              <div class="value">${formatInr(userSummary.totalBalance)}</div>
            </div>
            <div class="tile">
              <div class="label">Total Skipped</div>
              <div class="value">${userSummary.skippedMonths} Months</div>
            </div>
          </div>

          <h3>Transactions (${filteredTransactions.length})</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th style="text-align:right;">Amount</th>
                <th>Method</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>`;

    printInHiddenFrame(html);
  };

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h1
                        className="card-title"
                        style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                      >
                        Transaction Report
                      </h1>
                    </div>
                  </div>
                </div>

                <div className="card-body pt-0">
                  <div
                    className="d-flex justify-content-between align-items-center mb-3"
                    style={{ marginTop: "1rem" }}
                  >
                    <div className="col-md-6">
                      <label htmlFor="userSelect" className="form-label fw-semibold">
                        Select Member:
                      </label>
                      <Select
                        inputId="userSelect"
                        isLoading={loadingUsers}
                        options={userOptions}
                        value={selectedUser}
                        onChange={handleUserChange}
                        placeholder="Type name or phone..."
                        isClearable
                        classNamePrefix="select"
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        noOptionsMessage={() => (loadingUsers ? "Loading..." : "No users")}
                      />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {selectedUser && (
                        <button
                          type="button"
                          className="btn"
                          onClick={handleGeneratePdf}
                          style={{
                            backgroundColor: "#4B2C3B",
                            borderColor: "#4B2C3B",
                            color: "#fff",
                          }}
                        >
                          <i className="fas fa-file-pdf me-1" /> Generate PDF
                        </button>
                      )}
                      <select
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block w-auto"
                      >
                        {[5, 10, 20, 50].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                      <span className="ms-2">entries per page</span>
                    </div>
                  </div>

                  {/* Overall Summary */}
                  {overallSummary && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <div
                          className="report-summary-tiles"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                            gap: 14,
                          }}
                        >
                          {[
                            {
                              label: "Total Balance",
                              value: `₹${overallSummary.totalBalance.toLocaleString("en-IN")}`,
                              soft: dark ? "rgba(211,175,55,0.12)" : "rgba(211,175,55,0.14)",
                              accent: "#D3AF37",
                              valueColor: dark ? "#F0D78C" : "#4B2C3B",
                            },
                            {
                              label: "Installments",
                              value: `₹${overallSummary.totalPayments.toLocaleString("en-IN")}`,
                              soft: dark ? "rgba(15,118,110,0.16)" : "rgba(15,118,110,0.1)",
                              accent: "#0f766e",
                              valueColor: "#0f766e",
                            },
                            {
                              label: "Withdrawals",
                              value: `₹${overallSummary.totalWithdrawals.toLocaleString("en-IN")}`,
                              soft: dark ? "rgba(185,28,28,0.16)" : "rgba(185,28,28,0.08)",
                              accent: "#b91c1c",
                              valueColor: "#b91c1c",
                            },
                          ].map((item) => (
                            <div
                              key={item.label}
                              style={{
                                borderRadius: 16,
                                padding: "16px 18px 18px",
                                background: item.soft,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: dark
                                    ? "rgba(255,255,255,0.6)"
                                    : "rgba(75,44,59,0.55)",
                                  marginBottom: 10,
                                }}
                              >
                                {item.label}
                                <span
                                  style={{
                                    display: "block",
                                    width: 28,
                                    height: 2,
                                    marginTop: 6,
                                    borderRadius: 2,
                                    background: item.accent,
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  fontSize: "1.45rem",
                                  fontWeight: 700,
                                  color: item.valueColor,
                                  letterSpacing: "-0.02em",
                                  lineHeight: 1.15,
                                }}
                              >
                                {item.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Summary */}
                  {userSummary && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <div
                          className="report-summary-tiles"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 220px))",
                            gap: 14,
                          }}
                        >
                          <div
                            style={{
                              borderRadius: 16,
                              padding: "16px 18px 18px",
                              background: dark
                                ? "rgba(211,175,55,0.12)"
                                : "rgba(211,175,55,0.14)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: dark
                                  ? "rgba(255,255,255,0.6)"
                                  : "rgba(75,44,59,0.55)",
                                marginBottom: 10,
                              }}
                            >
                              Total Balance
                              <span
                                style={{
                                  display: "block",
                                  width: 28,
                                  height: 2,
                                  marginTop: 6,
                                  borderRadius: 2,
                                  background: "#D3AF37",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                fontSize: "1.45rem",
                                fontWeight: 700,
                                color: dark ? "#F0D78C" : "#4B2C3B",
                                letterSpacing: "-0.02em",
                                lineHeight: 1.15,
                              }}
                            >
                              ₹{userSummary.totalBalance.toLocaleString("en-IN")}
                            </div>
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: dark
                                  ? "rgba(255,255,255,0.4)"
                                  : "rgba(75,44,59,0.45)",
                              }}
                            >
                              ₹{userSummary.totalPayments.toLocaleString("en-IN")} − ₹
                              {userSummary.totalWithdrawals.toLocaleString("en-IN")}
                            </div>
                          </div>

                          <div
                            style={{
                              borderRadius: 16,
                              padding: "16px 18px 18px",
                              background: dark
                                ? "rgba(185,28,28,0.16)"
                                : "rgba(185,28,28,0.08)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: dark
                                  ? "rgba(255,255,255,0.6)"
                                  : "rgba(75,44,59,0.55)",
                                marginBottom: 10,
                              }}
                            >
                              Total Skipped
                              <span
                                style={{
                                  display: "block",
                                  width: 28,
                                  height: 2,
                                  marginTop: 6,
                                  borderRadius: 2,
                                  background: "#b91c1c",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                fontSize: "1.45rem",
                                fontWeight: 700,
                                color: "#b91c1c",
                                letterSpacing: "-0.02em",
                                lineHeight: 1.15,
                              }}
                            >
                              {userSummary.skippedMonths}
                            </div>
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: dark
                                  ? "rgba(255,255,255,0.4)"
                                  : "rgba(75,44,59,0.45)",
                              }}
                            >
                              Months
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="table-responsive">
                    {loadingTransactions ? (
                      <div className="text-center p-4">Loading transactions...</div>
                    ) : filteredTransactions.length > 0 ? (
                      <table className="table mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center px-3 py-3 border-0">Card No</th>
                            <th className="text-center px-3 py-3 border-0">Transaction Type</th>
                            <th className="text-center px-3 py-3 border-0">Amount</th>
                            <th className="text-center px-3 py-3 border-0">Method</th>
                            <th className="text-center px-3 py-3 border-0">Date</th>
                            <th className="text-center px-3 py-3 border-0">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTransactions.map((tx, idx) => {
                            const userIdStr =
                              tx.user_id !== undefined && tx.user_id !== null
                                ? String(tx.user_id)
                                : "";
                            const cardNo =
                              cardNumberByUserId[userIdStr] ||
                              tx.card_no ||
                              "";
                            const identifier =
                              cardNo
                                ? `#${cardNo}`
                                : `#${tx.user_id ?? idx + 1}`;
                            const color =
                              (tx.color_code && String(tx.color_code).trim()) ||
                              deriveColor(tx.transaction_type, tx.transaction_method);
                            const label = getTxnLabel(
                              tx.transaction_type,
                              tx.transaction_method
                            );

                            return (
                              <tr
                                key={tx.id ?? idx}
                                className="border-bottom"
                                style={{
                                  borderBottom: "1px solid var(--bs-border-color)",
                                  transition: "all 0.3s ease",
                                  backgroundColor: idx % 2 === 0 ? "transparent" : "var(--bs-gray-50)",
                                }}
                                onMouseEnter={(e) => {
                                  const tr = e.target.closest("tr");
                                  tr.style.backgroundColor = "var(--bs-primary-bg-subtle)";
                                  tr.style.transform = "translateY(-1px)";
                                  tr.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  const tr = e.target.closest("tr");
                                  const idx = Array.from(tr.parentNode.children).indexOf(tr);
                                  tr.style.backgroundColor = idx % 2 === 0 ? "transparent" : "var(--bs-gray-50)";
                                  tr.style.transform = "translateY(0px)";
                                  tr.style.boxShadow = "none";
                                }}
                              >
                                {/* Card Number Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-primary bg-gradient px-2 py-1"
                                    style={{
                                      fontSize: "0.8rem",
                                      fontWeight: "600",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    {identifier}
                                  </span>
                                </td>

                                {/* Transaction Type Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "0.45rem 0.9rem",
                                      borderRadius: "999px",
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                      lineHeight: 1,
                                      background: "transparent",
                                      border: `2px solid ${color}`,
                                      color,
                                      minWidth: 75,
                                      textAlign: "center",
                                    }}
                                    title={color}
                                  >
                                    {label}
                                  </span>
                                </td>

                                {/* Amount Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="badge bg-success bg-gradient px-3 py-2"
                                    style={{
                                      fontSize: "0.85rem",
                                      fontWeight: "600",
                                      borderRadius: "12px",
                                      boxShadow: "0 2px 8px rgba(40, 167, 69, 0.3)",
                                    }}
                                  >
                                    ₹{Number(tx.amount).toLocaleString("en-IN")}
                                  </span>
                                </td>

                                {/* Method Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  {(() => {
                                    const method = normalizeMethod(tx.transaction_method);
                                    return ["Cash", "UPI", "Bank Transfer", "Net Banking", "Metal"].includes(method) ? (
                                      <span
                                        className={`badge rounded-pill bg-transparent border ${
                                          method === "Metal"
                                            ? "border-warning text-warning"
                                            : "border-info text-info"
                                        }`}
                                        style={{ padding: "0.45rem 0.9rem", fontSize: "0.8rem", fontWeight: "600" }}
                                      >
                                        {method}
                                      </span>
                                    ) : (
                                      <span
                                        className="fw-medium"
                                        style={{
                                          fontSize: "0.85rem",
                                          color: "var(--bs-secondary-color)",
                                        }}
                                      >
                                        -
                                      </span>
                                    );
                                  })()}
                                </td>

                                {/* Date Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <span
                                    className="fw-medium"
                                    style={{
                                      fontSize: "0.85rem",
                                      color: "var(--bs-secondary-color)",
                                    }}
                                  >
                                    {tx.transaction_date || tx.payment_date || "N/A"}
                                  </span>
                                </td>

                                {/* Action Column */}
                                <td className="text-center px-3 py-3 border-0">
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm me-1"
                                      title="Edit Transaction"
                                      onClick={() => {
                                        if (
                                          String(tx.transaction_type || "").toLowerCase() ===
                                          "withdrawal"
                                        ) {
                                          setEditingWithdrawalId(tx.id);
                                        } else {
                                          setEditingPaymentId(tx.id);
                                        }
                                      }}
                                      style={{
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        transition: "all 0.2s ease",
                                        border: "1px solid #667eea",
                                        color: "#667eea",
                                        backgroundColor: "transparent",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#667eea";
                                        e.currentTarget.style.color = "white";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                        e.currentTarget.style.color = "#667eea";
                                        e.currentTarget.style.transform = "translateY(0px)";
                                        e.currentTarget.style.boxShadow = "none";
                                      }}
                                    >
                                      <i className="fas fa-edit" style={{ fontSize: "0.8rem" }}></i>
                                    </button>
                                    {String(tx.transaction_type || "").toLowerCase() ===
                                    "withdrawal" ? (
                                      <DeleteWithdrawal
                                        wId={tx.id}
                                        userName={getFullName(tx) || identifier}
                                        cardNo={cardNo}
                                        onDeleteSuccess={handleDeleteSuccess}
                                      />
                                    ) : (
                                      <DeletePayment
                                        pId={tx.id}
                                        userName={getFullName(tx) || identifier}
                                        cardNo={cardNo}
                                        onDeleteSuccess={handleDeleteSuccess}
                                      />
                                    )}
                                  </div>
                                </td>
                     
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : selectedUser ? (
                      <div className="text-center p-4">
                        <p className="text-muted">No transactions found for this user.</p>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <p className="text-muted">No transactions found.</p>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {filteredTransactions.length > 0 && (
                    <div className="d-flex justify-content-center align-items-center mt-3">
                      <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      >
                        Prev
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-outline-secondary btn-sm ms-2"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Payment Modal */}
      {editingPaymentId && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered animate__animated animate__zoomIn animate__faster">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{ backgroundColor: "#4B2C3B", color: "white" }}
              >
                <h5 className="modal-title">
                  Edit Payment (ID={editingPaymentId})
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingPaymentId(null)}
                />
              </div>
              <div className="modal-body">
                <EditPayment
                  paymentId={editingPaymentId}
                  onClose={() => {
                    setEditingPaymentId(null);
                    fetchReportData();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Withdrawal Modal */}
      {editingWithdrawalId && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered animate__animated animate__zoomIn animate__faster">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{ backgroundColor: "#4B2C3B", color: "white" }}
              >
                <h5 className="modal-title">
                  Edit Withdrawal (ID={editingWithdrawalId})
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingWithdrawalId(null)}
                />
              </div>
              <div className="modal-body">
                <EditWithdrawal
                  withdrawalId={editingWithdrawalId}
                  onClose={() => {
                    setEditingWithdrawalId(null);
                    fetchReportData();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .report-summary-tiles {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Report;
